/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import * as TabsPrimitive from '@radix-ui/react-tabs';

import { cn } from '@/lib/utils';

/**
 * Tabs — shadcn's Tabs on the Radix Tabs primitive, styled to Figma `6343:55485`
 * (CRCB2B-629).
 *
 * ## Measured treatment (`.Tab Item`, nodes 6343:55487 / 7651:44725 / 6343:55490)
 *
 * | State    | indicator                            | label                        |
 * |----------|--------------------------------------|------------------------------|
 * | inactive | 1px `--border` #d6d6d6               | `--text-tertiary` #676767    |
 * | hover    | 2px `--border-hover` #bcbcbc         | `--muted-foreground` #585858 |
 * | active   | 2px `--foreground` #000              | `--foreground` #000          |
 *
 * Every state is 44px tall with 16px inline padding, an 8px gap, and a `body/lg-bold`
 * label (18px, bold, line-height 1.5, 3% tracking).
 *
 * **The indicator is an `::after` bar, not a border, and that is deliberate.** A border
 * thickening from 1px to 2px shrinks the content box even inside a fixed `h-11`
 * border-box, which nudges the vertically-centred label up half a pixel on hover. An
 * absolutely positioned bar sits outside layout, so the label never moves — the type
 * holds perfectly still through every state change.
 *
 * **The rail is composed of the triggers' own indicators**, not a rule on the list. That
 * is what makes the inactive 1px line continuous while the active trigger thickens to
 * 2px without a second element.
 *
 * **Vertical keeps the indicator underneath each trigger.** Figma's `.Tab Item` parts
 * (`6350:55508`) only ever draw a bottom rule, so a vertical list is the same triggers
 * stacked — the indicator does not move to the inline-start edge. Only the axis and the
 * label alignment change.
 *
 * ## Selection is deliberately local state
 *
 * Tab selection is NOT written to the URL and does NOT deep-link, per CRCB2B-629's
 * Assumptions. This is a considered exception to the repo rule that URL-worthy state
 * belongs in `useSearchParams`: tabs segment related content *within* one page rather
 * than choosing between views, so the selection is not addressable. A control that
 * changes the URL is navigation and wants a `NavLink` set, not Tabs.
 */
function Tabs({ className, ...props }: React.ComponentProps<typeof TabsPrimitive.Root>) {
    return (
        <TabsPrimitive.Root
            data-slot="tabs"
            // `group/tabs` is load-bearing: Radix puts `data-orientation` on the Root and
            // the Content but NOT on the List or the Trigger, so those two read the
            // orientation through this group rather than their own attribute.
            //
            // The 24px gap between the rail and the panel is DERIVED — Figma's Tabs node
            // ships the rail and an overflow example but no panel, so it defines no
            // rail-to-content spacing.
            className={cn(
                'group/tabs flex flex-col gap-6 data-[orientation=vertical]:flex-row',
                className,
            )}
            {...props}
        />
    );
}

/**
 * The tab rail.
 *
 * Horizontal overflow scrolls the LIST and only the list, with the native scrollbar
 * Figma draws in the Overflow example (`11773:34119`) — no custom scrollbar, and the
 * content panel never scrolls horizontally as a consequence.
 *
 * `overflow-x-auto` makes this a scroll container, which axe's
 * `scrollable-region-focusable` rule requires be keyboard reachable. It is: the triggers
 * inside are focusable and Radix gives the list roving focus, so the region is operable
 * without adding a tabindex.
 */
function TabsList({ className, ...props }: React.ComponentProps<typeof TabsPrimitive.List>) {
    return (
        <TabsPrimitive.List
            data-slot="tabs-list"
            className={cn(
                'flex shrink-0 overflow-x-auto',
                // Vertical stacks the triggers, each keeping its own bottom indicator per
                // Figma. `overflow-y-hidden` pairs with the horizontal `overflow-x-auto`
                // because a single `overflow-x` value forces the other axis off `visible`,
                // which would otherwise show a stray vertical scrollbar.
                'overflow-y-hidden',
                'group-data-[orientation=vertical]/tabs:w-fit group-data-[orientation=vertical]/tabs:flex-col group-data-[orientation=vertical]/tabs:overflow-visible',
                className,
            )}
            {...props}
        />
    );
}

function TabsTrigger({ className, ...props }: React.ComponentProps<typeof TabsPrimitive.Trigger>) {
    return (
        <TabsPrimitive.Trigger
            data-slot="tabs-trigger"
            className={cn(
                'relative inline-flex h-11 shrink-0 items-center gap-2 rounded-ui px-4 py-3 text-lg leading-normal font-bold tracking-[0.03em] whitespace-nowrap transition-colors',
                // The indicator is a PSEUDO-ELEMENT, not a border. A border — even inside a
                // fixed `h-11` border-box — shrinks the content box when it thickens, which
                // nudges the vertically-centred label up by half a pixel on hover. An absolutely
                // positioned bar is outside layout entirely, so the text never moves at all.
                "after:absolute after:inset-x-0 after:bottom-0 after:content-[''] after:transition-all",
                // Focus is an inset OUTLINE, not the usual `focus-visible:border-ring`
                // ring pair, for two reasons. (1) The border here is the selected
                // indicator, and a focus rule that repaints it would collide with the
                // `data-[state=active]` rule at equal (0,2,0) specificity — the failure
                // mode `design-tokens` records, where class order silently decides which
                // wins. (2) A negative outline offset draws inside the box, so it cannot
                // be clipped by the list's scroll container the way an outset ring is.
                'outline-none focus-visible:outline-2 focus-visible:outline-ring focus-visible:-outline-offset-2',
                'disabled:pointer-events-none disabled:text-disabled-foreground',
                // Inactive resting: 1px --border rule, tertiary label.
                'text-text-tertiary after:h-px after:bg-border',
                // Hover applies to inactive triggers only — the active one is already at
                // its terminal treatment, and Figma designs no active-hover cell.
                'data-[state=inactive]:hover:text-muted-foreground data-[state=inactive]:hover:after:h-0.5 data-[state=inactive]:hover:after:bg-border-hover',
                'data-[state=active]:text-foreground data-[state=active]:after:h-0.5 data-[state=active]:after:bg-foreground',
                // Vertical stacks the triggers and keeps the indicator UNDERNEATH each one,
                // per Figma — the `.Tab Item` parts (`6350:55508`) only ever draw a bottom
                // rule, so there is no inline-start treatment to switch to. Only the axis and
                // the label alignment change.
                'group-data-[orientation=vertical]/tabs:w-full group-data-[orientation=vertical]/tabs:justify-start',
                className,
            )}
            {...props}
        />
    );
}

function TabsContent({ className, ...props }: React.ComponentProps<typeof TabsPrimitive.Content>) {
    return (
        <TabsPrimitive.Content
            data-slot="tabs-content"
            // `min-w-0` lets a wide panel child (a table, a product grid) shrink inside
            // the flex column instead of forcing the whole Tabs box wider — which is what
            // would otherwise push the horizontal overflow onto the page rather than the
            // rail.
            className={cn('min-w-0 flex-1 outline-none', className)}
            {...props}
        />
    );
}

export { Tabs, TabsList, TabsTrigger, TabsContent };
